import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import apiClient from '../services/api';
import { 
  ArrowLeft, Send, Loader2, AlertCircle, CheckCircle2,
  Image as ImageIcon, MessageCircle
} from 'lucide-react';
import { BACKEND_URL } from '../utils/config';

const PostUpdatePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [campaign, setCampaign] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image: ''
  });

  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        const response = await apiClient.get(`/campaigns/${id}`);
        setCampaign(response.data);
      } catch (err) {
        setError('Failed to load campaign');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCampaign();
    }
  }, [id]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await fetch(`${BACKEND_URL}/api/upload/image`, {
          method: 'POST',
          body: formDataUpload
        });
        
        const result = await response.json();
        if (result.success) {
          const imageUrl = `${BACKEND_URL}${result.url}`;
          setFormData(prev => ({ ...prev, image: imageUrl }));
        }
      } catch (err) {
        console.error('Upload error:', err);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.title.trim()) {
      setError('Please enter an update title');
      return;
    }
    if (!formData.content.trim()) {
      setError('Please enter update content');
      return;
    }

    setPosting(true);

    try {
      await apiClient.post(`/campaigns/${id}/updates`, {
        title: formData.title,
        content: formData.content,
        image: formData.image || null
      });
      
      setSuccess('Update posted successfully! Your donors will be notified.');
      
      // Redirect back to dashboard after short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to post update');
    } finally {
      setPosting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
              <p className="text-foreground mb-4">{t('pleaseLoginToPost')}</p>
              <Link to="/login">
                <Button className="bg-gradient-to-r from-emerald-500 to-teal-600">{t('signIn')}</Button>
              </Link>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <p className="text-foreground mb-4">{t('campaignNotFound')}</p>
              <Link to="/dashboard">
                <Button variant="outline">{t('backToDashboard')}</Button>
              </Link>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  // Check if user owns this campaign
  if (campaign.creator?.id !== user?.id) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <p className="text-foreground mb-4">{t('onlyPostToOwnCampaigns')}</p>
              <Link to="/dashboard">
                <Button variant="outline">{t('backToDashboard')}</Button>
              </Link>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 py-8">
        <div className="max-w-3xl mx-auto px-4">
          {/* Back Button */}
          <Link to={`/campaign/${id}`} className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" /> {t('backToCampaign')}
          </Link>

          {/* Campaign Info */}
          <div className="mb-6 p-4 bg-secondary rounded-lg flex items-center gap-4">
            <img 
              src={campaign.image} 
              alt={campaign.title}
              className="w-16 h-16 rounded-lg object-cover"
            />
            <div>
              <p className="text-sm text-muted-foreground">{t('postingUpdateTo')}</p>
              <h3 className="font-semibold text-foreground">{campaign.title}</h3>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <MessageCircle className="w-6 h-6 text-emerald-400" />
                {t('postCampaignUpdate')}
              </CardTitle>
              <CardDescription>
                {t('keepDonorsInformed')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {success && (
                  <Alert className="bg-emerald-500/10 border-emerald-500/30">
                    <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                    <AlertDescription className="text-emerald-400">{success}</AlertDescription>
                  </Alert>
                )}

                {/* Title */}
                <div>
                  <Label htmlFor="title">{t('updateTitle')}</Label>
                  <Input
                    id="title"
                    placeholder={t('updateTitlePlaceholder')}
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="mt-2 bg-secondary"
                  />
                </div>

                {/* Content */}
                <div>
                  <Label htmlFor="content">{t('updateContent')}</Label>
                  <Textarea
                    id="content"
                    placeholder={t('updateContentPlaceholder')}
                    value={formData.content}
                    onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                    className="mt-2 min-h-[200px] bg-secondary"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    {formData.content.length} / 2000 {t('characters')}
                  </p>
                </div>

                {/* Image (Optional) */}
                <div>
                  <Label>{t('addPhotoOptional')}</Label>
                  <input
                    type="file"
                    id="updateImageInput"
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                  <div 
                    className="mt-2 border-2 border-dashed border-border rounded-xl p-4 text-center hover:border-emerald-500/50 transition-colors cursor-pointer bg-secondary/50"
                    onClick={() => document.getElementById('updateImageInput').click()}
                  >
                    {formData.image ? (
                      <div className="relative">
                        <img 
                          src={formData.image} 
                          alt="Update" 
                          className="w-full h-48 object-cover rounded-lg"
                        />
                        <Button 
                          type="button"
                          variant="outline" 
                          className="mt-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFormData(prev => ({ ...prev, image: '' }));
                          }}
                        >
                          {t('removePhoto')}
                        </Button>
                      </div>
                    ) : (
                      <>
                        <ImageIcon className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">{t('clickToAddPhoto')}</p>
                      </>
                    )}
                  </div>
                </div>

                {/* Submit */}
                <div className="flex gap-4">
                  <Button 
                    type="submit" 
                    disabled={posting}
                    className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:opacity-90"
                  >
                    {posting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('posting')}
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" /> {t('postUpdate')}
                      </>
                    )}
                  </Button>
                  <Link to={`/campaign/${id}`}>
                    <Button type="button" variant="outline">{t('cancel')}</Button>
                  </Link>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PostUpdatePage;
